/**
 * @fileoverview React Context for real-time state management.
 *
 * This context provides socket connection state, presence awareness,
 * and event subscription to all components in the app.
 *
 * @module contexts/RealtimeContext
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    setTokenGetter,
    initializeSocket,
    getSocket,
    getSocketId,
    joinBudget,
    leaveBudget,
    viewMonth,
    disconnectSocket,
    subscribe,
    isConnected as checkIsConnected,
    sendHeartbeat,
} from '../services/socket';
import { setSocketIdGetter } from '../services/api';

/** Heartbeat interval in milliseconds (2 minutes) */
const HEARTBEAT_INTERVAL = 2 * 60 * 1000;

/**
 * @typedef {Object} RealtimeContextValue
 * @property {boolean} isConnected - Whether socket is connected
 * @property {string|null} connectionError - Error message if connection failed
 * @property {Array} usersInBudget - List of users in current budget
 * @property {Function} joinBudgetRoom - Join a budget room
 * @property {Function} leaveBudgetRoom - Leave current budget room
 * @property {Function} updateMonthView - Update current month view
 * @property {Function} subscribeToEvent - Subscribe to a socket event
 */

const RealtimeContext = createContext(null);

/**
 * Provider component for real-time features.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export function RealtimeProvider({ children }) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();

    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [usersInBudget, setUsersInBudget] = useState([]);

    const currentBudgetRef = useRef(null);
    const currentMonthRef = useRef(null);
    const eventListenersRef = useRef(new Map());
    const isInitializingRef = useRef(false);
    const heartbeatIntervalRef = useRef(null);

    /**
     * Set up socket event listeners.
     * Defined before useEffect to avoid temporal dead zone.
     */
    const setupEventListeners = useCallback(() => {
        const socket = getSocket();
        if (!socket) return;

        // Presence events
        socket.on('presence:update', (data) => {
            setUsersInBudget(data.users || []);
        });

        socket.on('presence:user_joined', (data) => {
            setUsersInBudget(prev => {
                // Avoid duplicates
                if (prev.some(u => u.userId === data.userId)) {
                    return prev;
                }
                return [...prev, data];
            });
        });

        socket.on('presence:user_left', (data) => {
            setUsersInBudget(prev => prev.filter(u => u.userId !== data.userId));
        });

        socket.on('presence:editing', (data) => {
            setUsersInBudget(prev => prev.map(u =>
                u.userId === data.userId
                    ? { ...u, editing: { type: data.resourceType, key: data.resourceKey } }
                    : u
            ));
        });

        socket.on('presence:stopped_editing', (data) => {
            setUsersInBudget(prev => prev.map(u =>
                u.userId === data.userId
                    ? { ...u, editing: null }
                    : u
            ));
        });

        // Connection state events
        socket.on('connect', () => {
            setIsConnected(true);
            setConnectionError(null);

            // Set up socket ID getter for API requests (enables same-user multi-device sync)
            setSocketIdGetter(getSocketId);

            // Rejoin budget room on reconnection
            if (currentBudgetRef.current) {
                joinBudget(currentBudgetRef.current);
            }

            // Start periodic heartbeat to keep presence alive
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
            heartbeatIntervalRef.current = setInterval(() => {
                sendHeartbeat();
            }, HEARTBEAT_INTERVAL);
        });

        socket.on('disconnect', () => {
            setIsConnected(false);

            // Stop heartbeat when disconnected
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
        });

        socket.on('connect_error', (error) => {
            setConnectionError(error.message);
        });

        // Replay any queued event subscriptions that were registered before socket connected
        eventListenersRef.current.forEach((callbacks, event) => {
            callbacks.forEach(callback => {
                console.log(`[Realtime] Replaying queued subscription for: ${event}`);
                socket.on(event, callback);
            });
        });
        // Clear the queue after replaying
        eventListenersRef.current.clear();
    }, []);

    // Initialize socket connection when authenticated
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        // Set up token getter for socket auth
        setTokenGetter(getAccessTokenSilently);

        let mounted = true;

        const connect = async () => {
            // Use ref to prevent multiple simultaneous connection attempts
            if (isInitializingRef.current) return;

            isInitializingRef.current = true;
            try {
                console.log('[Realtime] Attempting to connect...');
                await initializeSocket();
                if (mounted) {
                    setIsConnected(true);
                    setConnectionError(null);
                    setupEventListeners();
                    console.log('[Realtime] Connected successfully');
                }
            } catch (error) {
                console.error('[Realtime] Connection failed:', error);
                if (mounted) {
                    setConnectionError(error.message);
                    setIsConnected(false);
                }
            } finally {
                isInitializingRef.current = false;
            }
        };

        connect();

        return () => {
            mounted = false;
            // Clear heartbeat interval on cleanup
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
            }
            disconnectSocket();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, getAccessTokenSilently]);

    /**
     * Join a budget room to receive updates.
     */
    const joinBudgetRoom = useCallback((budgetKey) => {
        // Leave previous budget if different
        if (currentBudgetRef.current && currentBudgetRef.current !== budgetKey) {
            leaveBudget();
            setUsersInBudget([]);
        }

        currentBudgetRef.current = budgetKey;
        joinBudget(budgetKey);
    }, []);

    /**
     * Leave the current budget room.
     */
    const leaveBudgetRoom = useCallback(() => {
        leaveBudget();
        currentBudgetRef.current = null;
        currentMonthRef.current = null;
        setUsersInBudget([]);
    }, []);

    /**
     * Update the current month view.
     */
    const updateMonthView = useCallback((monthKey) => {
        currentMonthRef.current = monthKey;
        viewMonth(monthKey);
    }, []);

    /**
     * Subscribe to a socket event.
     *
     * @param {string} event - Event name
     * @param {Function} callback - Event handler
     * @returns {Function} Unsubscribe function
     */
    const subscribeToEvent = useCallback((event, callback) => {
        const socket = getSocket();
        if (!socket) {
            // Queue the subscription for when socket connects
            const existing = eventListenersRef.current.get(event) || [];
            existing.push(callback);
            eventListenersRef.current.set(event, existing);
            return () => {
                const listeners = eventListenersRef.current.get(event) || [];
                eventListenersRef.current.set(event, listeners.filter(l => l !== callback));
            };
        }

        return subscribe(event, callback);
    }, []);

    const value = {
        isConnected,
        connectionError,
        usersInBudget,
        joinBudgetRoom,
        leaveBudgetRoom,
        updateMonthView,
        subscribeToEvent,
        checkIsConnected,
        getSocketId,
    };

    return (
        <RealtimeContext.Provider value={value}>
            {children}
        </RealtimeContext.Provider>
    );
}

/**
 * Hook to access realtime context.
 *
 * @returns {RealtimeContextValue} Realtime context value
 * @throws {Error} If used outside of RealtimeProvider
 */
export function useRealtime() {
    const context = useContext(RealtimeContext);
    if (!context) {
        throw new Error('useRealtime must be used within a RealtimeProvider');
    }
    return context;
}

export default RealtimeContext;
